import React, { useContext, useEffect, useState } from "react";
import StylishButton from "../../common/StylishButton";
import { ReactComponent as PlaySvg } from "../../../assets/icons/play.svg";
import { ChoiceChip } from "../../common/ChoiceChip";
import useWorks from "../../../hooks/useWorks";
import useUsers from "../../../hooks/useUsers";
import useLandingPage from "../../../hooks/useLandingPage";
import { PAGE_STATE, langComponent } from "../../../constants";
import AppContext from "../../../AppContext";
import ShoppingContext from "../../../contexts/ShoppingContext";
import ProjectCard from "../../ProjectCard";
import { startOfDay } from "date-fns";

import "../../../styles/landingPage/trending-now.scss";
import ContentLoader from "react-content-loader";
import SkeletonCard from "../../common/Skeleton/Card";
import useLikes from "../../../hooks/useLikes";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import ScrollDownIndicator from "../../common/ScrollDownIndicator";

const TrendingNow = (props) => {
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [works, setWorks] = useState(undefined);
  const { designStyles, getLang, isAuth, user } = useContext(AppContext);
  const { setShoppingFilter } = useContext(ShoppingContext);
  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState();
  const { queryWorks } = useWorks();
  const { queryUsers } = useUsers();
  const { queryLikes } = useLikes();
  const { queryLandingPageConfigs } = useLandingPage();
  const [workStore, setWorkStore] = useState([]);
  const TranslationWorkType = getLang(langComponent.workType);

  useEffect(() => {
    initWork();
  }, []);

  useEffect(() => {
    if (!designStyles || !designStyles.works) return;

    const _tabs = [
      designStyles.works.find((w) => w.name === "Interior Design"),
      designStyles.works.find((w) => w.name === "Architecture"),
      designStyles.works.find((w) => w.name === "Landscape"),
      designStyles.works.find((w) => w.name === "Photographer"),
    ];

    setTabs(_tabs);
    setSelectedTab(_tabs[0].value);
    return () => {};
  }, [designStyles]);

  useEffect(() => {
    if (!selectedTab) return;
    fetchData();
    return () => {};
  }, [selectedTab]);

  const initWork = async () => {
    // * Prepare work data from landingPageConfig
    const _businessDate = startOfDay(new Date());

    const { data: landingPages } = await queryLandingPageConfigs({
      effectiveDate: {
        $lte: _businessDate,
      },
      dueDate: {
        $gte: _businessDate,
      },
      section: 2,
    });

    if (landingPages.length === 0) return;

    const workIds = landingPages.map((m) => {
      return m.work;
    });

    const filterWork = {
      _id: { $in: workIds },
      active: true,
      deleted: false,
    };

    const { data: works } = await queryWorks(filterWork, undefined, undefined, {
      _id: 1,
      banner: 1,
      name: 1,
      createdBy: 1,
      designType: 1,
      desc: 1,
      size: 1,
      views: 1,
      type: 1,
      program: 1,
      renderer: 1,
      score: 1,
    });

    if (works.length === 0) return;
    setWorkStore(works);
  };

  const prepareWork = async () => {
    try {
      // * Get work by selectedTab
      const works = [];
      for (const _workStore of workStore) {
        if (_workStore.designType.work !== selectedTab) continue;
        works.push(_workStore);
      }

      if (works.length === 0) {
        const filterWork = {
          "designType.work": selectedTab,
          active: true,
          deleted: false,
        };

        const { data: workData } = await queryWorks(
          filterWork,
          0,
          7,
          {
            _id: 1,
            banner: 1,
            name: 1,
            createdBy: 1,
            designType: 1,
            desc: 1,
            size: 1,
            views: 1,
            type: 1,
            program: 1,
            renderer: 1,
            score: 1,
          },
          {
            createdOn: -1,
          }
        );

        setWorkStore([...workStore, ...workData]);
        return workData;
      }

      const tempWork = [];
      for (const work of works) {
        if (tempWork.length === 7) continue;
        tempWork.push(work);
      }

      if (tempWork.length === 7) return tempWork;
      const tempWorkIds = tempWork.map((m) => {
        return m._id;
      });

      const filterWorkRepair = {
        _id: { $nin: tempWorkIds },
        "designType.work": selectedTab,
        active: true,
        deleted: false,
      };

      const repairCount = 7 - tempWork.length;
      const { data: workRepair } = await queryWorks(
        filterWorkRepair,
        0,
        repairCount,
        {
          _id: 1,
          banner: 1,
          createdBy: 1,
          designType: 1,
          desc: 1,
          size: 1,
          type: 1,
          program: 1,
          renderer: 1,
          score: 1,
        }
      );

      setWorkStore([...workStore, ...workRepair]);
      return [...tempWork, ...workRepair];
    } catch (error) {
      console.error(error);
    }
    return [];
  };

  const fetchData = async () => {
    setPageState(PAGE_STATE.LOADING);
    const _works = await prepareWork();

    if (_works.length === 0) return;

    const _userIds = _works.map((w) => w.createdBy);

    // * Get users
    const { data: _users } = await queryUsers({ _id: { $in: _userIds } });

    let _likes = [];

    if (isAuth) {
      const result = await queryLikes(
        {
          user: user._id,
        },
        undefined,
        -1
      );

      _likes = result.data.length > 0 ? result.data[0].likes : [];
    }

    // * Map work with users and their ratings
    const results = [];
    for (const work of _works) {
      const user = _users.find((u) => u._id === work.createdBy);

      if (!user) continue;

      results.push({
        _id: work._id,
        banner: work.banner,
        panoramaExist: !!work.panorama,
        name: work.name,
        desc: work.desc,
        area: work.size,
        designType: work.designType,
        creator: {
          _id: user._id,
          avatar: user.avatar,
          name: `${user.firstname} ${user.surname || ""}`,
          rating: work.score ?? 0,
          plan: user.plan,
        },
        isLiked: _likes.find((l) => l === work._id) !== undefined,
        views: work.views ?? 0,
        type: work.type,
        program: work.program,
        renderer: work.renderer,
        score: work.score,
      });
    }

    setWorks(results);
    setPageState(PAGE_STATE.IDLE);
  };

  const handleTabClick = (tabValue) => {
    setSelectedTab(tabValue);
  };

  const handleSeeMoreClick = () => {
    setShoppingFilter({
      typeOfWork: selectedTab,
      sort: { createdOn: -1 },
    });

    props.onSeeMore();
  };

  const isLoaded = works && works.length > 0;

  return (
    <div className="lp-trending-now">
      <div className="lp-trending-now-header">
        <div className="lp-trending-now-header-section">
          <h2 className="lp-trending-now__title">Trending Now!</h2>
          <StylishButton
            label="See more"
            icon={<PlaySvg />}
            onClick={handleSeeMoreClick}
            size="small"
          />
          <div className="lp-trending-now-filters">
            {tabs.map((tab) => {
              const isActive = selectedTab === tab.value;

              return (
                <ChoiceChip
                  key={tab.value}
                  label={TranslationWorkType(tab.name).replace(" Design", "")}
                  onClick={() => handleTabClick(tab.value)}
                  isSelected={isActive}
                />
              );
            })}
          </div>
        </div>
        <div className="lp-trending-now-header-section">
          {isLoaded ? (
            <ProjectCard {...works[0]} />
          ) : (
            <SkeletonCard borderRadius={20} />
          )}
        </div>
      </div>
      <div className="lp-trending-now-list">
        {isLoaded ? (
          <Swiper
            modules={[Autoplay, Pagination]}
            slidesPerView={2}
            spaceBetween={20}
            autoplay={{
              delay: 5000,
              pauseOnMouseEnter: true,
            }}
            loop={true}
            speed={1000}
            breakpoints={{
              1280: {
                slidesPerView: 3,
              },
            }}
            pagination={{
              clickable: true,
            }}
            centerInsufficientSlides={true}
          >
            {works.slice(1).map((work) => (
              <SwiperSlide key={work._id}>
                <ProjectCard {...work} />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="lp-trending-now-list-loading-placeholder">
            <ContentLoader
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
            >
              <rect x="0" y="0" rx={20} ry={20} width="32%" height="100%" />
              <rect x="34%" y="0" rx={20} ry={20} width="32%" height="100%" />
              <rect x="68%" y="0" rx={20} ry={20} width="32%" height="100%" />
            </ContentLoader>
          </div>
        )}
      </div>
      <div className="lp-trending-now__scroll-down">
        <ScrollDownIndicator />
      </div>
    </div>
  );
};

export default TrendingNow;
